import React, { useEffect, useMemo, useState } from 'react';
import { useEntities } from '../../../hooks/useSWR';
import { fetcher } from '../../../hooks/useSWR';
import GenericFormsPage from '../../../components/generic/GenericFormsPage';
import { initializeModule } from '../../../utils/configInit';
import { myProceduresConfig, REVISION_ENTITY_TYPE_ID } from './myProceduresConfig';
import { createMyProceduresValidationSchema } from './myProceduresValidation';
import { mapProcedureAssignmentToEntity } from './mapProcedureAssignment';

const { config: baseConfig } = initializeModule(
  myProceduresConfig,
  createMyProceduresValidationSchema
);

/**
 * My Procedures — GenericFormsPage with assignment list and revision file viewing.
 */
const MyProceduresModulePage = () => {
  const { data: assignments = [], error, isLoading } = useEntities(
    baseConfig.api.baseEndpoint
  );

  const [enrichedEntities, setEnrichedEntities] = useState([]);

  const mappedEntities = useMemo(
    () => (Array.isArray(assignments) ? assignments.map(mapProcedureAssignmentToEntity) : []),
    [assignments]
  );

  useEffect(() => {
    let cancelled = false;

    const loadFileCounts = async () => {
      if (!mappedEntities.length) {
        setEnrichedEntities([]);
        return;
      }

      const rows = await Promise.all(
        mappedEntities.map(async (row) => {
          if (!row.canView || !row.currentRevisionId) {
            return row;
          }
          try {
            const files = await fetcher(
              `/file-attachment/entity/${REVISION_ENTITY_TYPE_ID}/${row.currentRevisionId}`
            );
            const active = (files || []).filter((f) => !f.isDeleted);
            return {
              ...row,
              fileAttachments: active,
              fileAttachmentCount: active.length,
            };
          } catch {
            return row;
          }
        })
      );

      if (!cancelled) {
        setEnrichedEntities(rows);
      }
    };

    loadFileCounts();
    return () => {
      cancelled = true;
    };
  }, [mappedEntities]);

  const displayData = enrichedEntities.length ? enrichedEntities : mappedEntities;

  const configWithData = useMemo(
    () => ({
      ...baseConfig,
      data: displayData,
      onSave: async () => {
        throw new Error('My Procedures is read-only.');
      },
      onDelete: async () => {
        throw new Error('My Procedures is read-only.');
      },
      customHandlers: baseConfig.customHandlers || {},
    }),
    [displayData]
  );

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error loading My Procedures: {error.message}
        </div>
      </div>
    );
  }

  if (isLoading && !displayData.length) {
    return (
      <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading My Procedures…</div>
    );
  }

  return <GenericFormsPage config={configWithData} />;
};

export default MyProceduresModulePage;
